import React from 'react';
import classNames from 'classnames';
import styles from './Pill.module.scss';
import { ContactAvatar, ContactAvatarGroup } from '@repo/components/avatars';
import Contact from '@repo/utils/models/Contact';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import { XIcon } from '@repo/icons';
import { useSelector } from 'react-redux';
import { selectContact, selectContactArray } from '@repo/redux/actions/contacts';

interface PillProps extends React.HTMLAttributes<HTMLSpanElement> {
  contact?: Contact;
  contactGroup?: Array<Contact>;
  clickable?: boolean;
  editable?: boolean;
  maxWidth?: number;
}

const calculateMaxWidthForName = (
  maxWidth: number,
  editable: boolean,
  contact?: Contact,
): number => {
  let _maxWidth = maxWidth - 18;

  if (editable) {
    _maxWidth = _maxWidth - 17;
  }

  if (contact && contact.avatarURL) {
    _maxWidth = _maxWidth - 24;
  }

  return _maxWidth;
};

export const ConnectedPill = ({ contact, contactGroup, ...props }: PillProps) => {
  const _contact = contact ? useSelector(selectContact(contact.id)) : undefined;
  const _contactGroup = contactGroup
    ? useSelector(selectContactArray(contactGroup?.map((c) => c.id)))
    : undefined;

  return <Pill contact={_contact} contactGroup={_contactGroup} {...props} />;
};

const PillDisplay = ({ contact, contactGroup, maxWidth, editable, ...props }: PillProps) => {
  let avatar = <ContactAvatar size="sm" className={styles.avatar} contact={contact} />;
  let label = contact?.displayName || contact?.display_name;

  if (contactGroup) {
    avatar = <ContactAvatarGroup contacts={contactGroup} />;
    label = `${contactGroup.length} People`;
  }

  return (
    <span {...props}>
      {avatar}
      <span className={styles.name} style={maxWidth && { maxWidth }}>
        {label}
      </span>
      {!editable ? null : (
        <XIcon className={styles.remove} width={11} height={11} color="textSecondary" />
      )}
    </span>
  );
};

const ClickablePill = ({ contact, contactGroup, ...props }: PillProps) => {
  const navigate = useNavigate();
  const onClick = (e) => {
    props.onClick?.(e);
    navigate(`/contact/${contact.id}`);
  };

  return (
    <PillDisplay
      contact={contact}
      contactGroup={contactGroup}
      {...props}
      onClick={onClick}
      className={classNames(props.className, styles.clickable)}
    />
  );
};

export function Pill({
  contact,
  contactGroup,
  maxWidth,
  clickable = false,
  editable = false,
  className = null,
  ...props
}: PillProps) {
  const isInRouter = useInRouterContext();
  className = classNames(styles.pill, (clickable || editable) && styles.clickable, className);
  maxWidth = maxWidth && calculateMaxWidthForName(maxWidth, editable, contact);

  if (isInRouter && clickable) {
    return (
      <ClickablePill
        contact={contact}
        contactGroup={contactGroup}
        maxWidth={maxWidth}
        editable={editable}
        className={className}
        {...props}
      />
    );
  }

  return (
    <PillDisplay
      contact={contact}
      contactGroup={contactGroup}
      maxWidth={maxWidth}
      editable={editable}
      className={className}
      {...props}
    />
  );
}
